"use client";

import * as React from "react";

import Multimedia from "@stadium/shared/src/components/shared/multimedia";
import CarouselContainer, {
  type SlidesPerRow,
} from "@stadium/ui/src/components/carousel-container";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

export interface BlogCard {
  image: string;
  imageAlt: string;
  category?: string;
  title: string;
  description: string;
}

export interface BlogCardsProps {
  cards: BlogCard[];
}

type VariantConfig = {
  slidesPerRow: SlidesPerRow;
  showCategory: boolean;
  useMdScrollTrack: boolean;
};

const VARIANT_CONFIG: Record<"BE03" | "BE04" | "BE05", VariantConfig> = {
  BE03: {
    slidesPerRow: {
      slidesPerRow: { slides: 3, rows: 1 },
      superLargeDesktop: { slides: 3, rows: 1 },
      web: { slides: 3, rows: 1 },
      ipad: { slides: 3, rows: 1 },
      mobile: { slides: 1, rows: 1 },
    },
    showCategory: true,
    useMdScrollTrack: true,
  },
  BE04: {
    slidesPerRow: {
      slidesPerRow: { slides: 3, rows: 1 },
      superLargeDesktop: { slides: 3, rows: 1 },
      web: { slides: 3, rows: 1 },
      ipad: { slides: 3, rows: 1 },
      mobile: { slides: 1, rows: 1 },
    },
    showCategory: false,
    useMdScrollTrack: true,
  },
  BE05: {
    slidesPerRow: {
      slidesPerRow: { slides: 2, rows: 1 },
      superLargeDesktop: { slides: 2, rows: 1 },
      web: { slides: 2, rows: 1 },
      ipad: { slides: 2, rows: 1 },
      mobile: { slides: 1, rows: 1 },
    },
    showCategory: false,
    useMdScrollTrack: false,
  },
};

export interface BlogCardsBaseProps extends BlogCardsProps {
  variant: "BE03" | "BE04" | "BE05";
  styles: Record<string, string>;
}

const BlogCardsBase: React.FC<Readonly<BlogCardsBaseProps>> = ({ variant, cards, styles }) => {
  const { slidesPerRow, showCategory, useMdScrollTrack } = VARIANT_CONFIG[variant];

  const { isMobileDevice, isTabletDevice, isDesktopDevice } =
    React.useContext(CurrentDeviceContext);

  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const scrollThumbRef = React.useRef<HTMLDivElement>(null);

  const updateScrollThumb = React.useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    const scrollThumb = scrollThumbRef.current;
    if (!scrollContainer || !scrollThumb) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
    const maxScroll = scrollWidth - clientWidth;
    if (maxScroll <= 0) return;

    const progress = scrollLeft / maxScroll;
    const trackWidth = scrollThumb.parentElement?.offsetWidth || 0;
    const thumbWidth = scrollThumb.offsetWidth;

    scrollThumb.style.transform = `translateX(${progress * (trackWidth - thumbWidth)}px)`;
  }, []);

  React.useEffect(() => {
    if (!useMdScrollTrack || !isTabletDevice) return;

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    updateScrollThumb();
    scrollContainer.addEventListener("scroll", updateScrollThumb);
    window.addEventListener("resize", updateScrollThumb);

    return () => {
      scrollContainer.removeEventListener("scroll", updateScrollThumb);
      window.removeEventListener("resize", updateScrollThumb);
    };
  }, [isTabletDevice, useMdScrollTrack, updateScrollThumb]);

  const renderCard = (card: BlogCard, index: number) => (
    <div key={`${card.title}-${index}`} className={styles.card}>
      {showCategory && card.category && <p className={styles.category}>{card.category}</p>}
      <div className={styles.cardContent}>
        <div className={styles.imageWrapper}>
          <Multimedia
            type="image"
            src={card.image}
            alt={card.imageAlt}
            className={styles.cardImage}
          />
        </div>
        <div className={styles.textContent}>
          <h3 className={styles.cardTitle}>{card.title}</h3>
          <p className={styles.cardDescription}>{card.description}</p>
        </div>
      </div>
    </div>
  );

  // ✅ BE05 → mobile: column, tablet+desktop: carousel
  if (!useMdScrollTrack) {
    if (isMobileDevice) {
      return <div className={styles.mobileCardsContainer}>{cards.map(renderCard)}</div>;
    }

    return (
      <CarouselContainer slidesPerRowValues={slidesPerRow} className={styles.carouselContainer}>
        {cards.map(renderCard)}
      </CarouselContainer>
    );
  }

  // ✅ BE03 / BE04
  if (isMobileDevice) {
    return <div className={styles.mobileCardsContainer}>{cards.map(renderCard)}</div>;
  }

  if (isTabletDevice) {
    return (
      <>
        <div ref={scrollContainerRef} className={styles.mdScrollContainer}>
          {cards.map((card, index) => (
            <div key={index} className={styles.mdCardWrapper}>
              {renderCard(card, index)}
            </div>
          ))}
        </div>

        <div className={styles.scrollTrack}>
          <div ref={scrollThumbRef} className={styles.scrollThumb} />
        </div>
      </>
    );
  }

  if (isDesktopDevice) {
    return (
      <CarouselContainer slidesPerRowValues={slidesPerRow} className={styles.carouselContainer}>
        {cards.map(renderCard)}
      </CarouselContainer>
    );
  }

  return null;
};

export default BlogCardsBase;
